/**
 * InvoicePackPrint — 月次 請求書パック（非TECHS 直接購入）。
 * Opens at /shizai/invoice-pack?year=2026&month=6 and prints:
 *   1) 表紙（索引）: 年月 / 件数 / 合計金額 / 1購入=1行、請求書未添付は赤フラグ、
 *      請求書日付未設定の要修正リストも併記（どの月の合計にも入れない）。
 *   2) 各依頼の請求書添付を1枚ずつ。画像はそのまま <img>、PDF は
 *      /api/shizai/pic-invoice-pack/page/{photo_id}/{n} が返す PNG を1ページ1枚。
 * 読み取り専用 — 何も保存しない。PicBatchPrint の描画パターンを踏襲。
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { shizaiApi } from './shizaiApi';

const BASE = process.env.REACT_APP_BACKEND_URL;
const yen = (n) => `¥${Math.round(Number(n) || 0).toLocaleString('ja-JP')}`;

function CoverRow({ r, i }) {
  const foreign = r.currency && r.currency !== 'JPY';
  return (
    <tr className="border-b border-zinc-200 align-top" data-testid={`pack-row-${r.request_no}`}>
      <td className="py-1.5 pr-1 text-right tabular-nums text-zinc-400">{i + 1}</td>
      <td className="py-1.5 pr-1 tabular-nums">{(r.invoice_date || '—').slice(5)}</td>
      <td className="py-1.5 pr-1 font-medium">{r.request_no}</td>
      <td className="py-1.5 pr-1">
        <span className="block truncate">{r.item_summary}</span>
        <span className="text-[9px] text-zinc-500">
          {r.line_count > 1 ? `他${r.line_count - 1}件 · ` : ''}{r.method_label}
          {r.vendor ? ` · ${r.vendor}` : ''}
        </span>
      </td>
      <td className="py-1.5 pr-1">
        <span className="block truncate">{r.invoice_no || '—'}</span>
        {foreign && (
          <span className="text-[9px] text-indigo-700">
            {r.currency}{r.fx_rate ? ` @${Number(r.fx_rate).toFixed(2)}` : ''}
          </span>
        )}
      </td>
      <td className="py-1.5 pr-1 text-right tabular-nums">{yen(r.amount)}</td>
      <td className="py-1.5 pr-1 text-right tabular-nums">
        {r.extra_cost_total_jpy ? yen(r.extra_cost_total_jpy) : '—'}
      </td>
      <td className="py-1.5 pr-1 text-right tabular-nums">
        {r.tax_amount_jpy ? yen(r.tax_amount_jpy) : '—'}
        <span className="block text-[9px] text-zinc-500">
          {r.tax_rate ? `${r.tax_rate}% ` : ''}{r.tax_mode_label === '税区分未設定' ? '未設定' : r.tax_mode_label}
        </span>
      </td>
      <td className="py-1.5 pr-1 text-right tabular-nums font-semibold">{yen(r.paid_amount)}</td>
      <td className="py-1.5 text-center">
        {r.has_invoice_file
          ? <span className="text-emerald-700">✓</span>
          : <span className="font-bold text-rose-600" data-testid={`pack-noinvoice-${r.request_no}`}>未添付</span>}
      </td>
    </tr>
  );
}

const COLS = ['3%', '7%', '12%', '25%', '15%', '10%', '9%', '8%', '11%', '5%'];

export default function InvoicePackPrint() {
  const [params] = useSearchParams();
  const now = new Date();
  const year = Number(params.get('year')) || now.getFullYear();
  const month = Number(params.get('month')) || now.getMonth() + 1;
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loadedImgs, setLoadedImgs] = useState(0);
  const [replacing, setReplacing] = useState(null);

  const load = useCallback(() => shizaiApi.invoicePack(year, month)
    .then((d) => { setData(d); setLoadedImgs(0); })
    .catch((e) => setError(e?.response?.data?.detail || '請求書パックを読み込めませんでした')),
  [year, month]);

  useEffect(() => { load(); }, [load]);

  // 保存先から読めなくなった請求書は、手元のPDFを入れ直せば印刷を通せる。
  const replaceInvoice = async (requestId, file) => {
    if (!file) return;
    setReplacing(requestId);
    try {
      await shizaiApi.replaceInvoice(requestId, file);
      toast.success('請求書を差し替えました — 読み込み直します');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '差し替えに失敗しました');
    } finally { setReplacing(null); }
  };

  // 添付1件につき、画像=1枚 / PDF=ページ数ぶんのシートを作る
  const sheets = useMemo(() => {
    const out = [];
    (data?.rows || []).forEach((r) => {
      (r.invoice_files || []).forEach((f) => {
        if (f.is_pdf && f.pages > 0) {
          for (let p = 1; p <= f.pages; p += 1) {
            out.push({ row: r, file: f, page: p, src: `${BASE}/api/shizai/pic-invoice-pack/page/${f.photo_id}/${p}` });
          }
        } else if (f.is_pdf) {
          out.push({ row: r, file: f, page: null, src: null });   // 展開できなかったPDF
        } else {
          out.push({ row: r, file: f, page: null, src: `${BASE}${f.url}` });
        }
      });
    });
    return out;
  }, [data]);

  const imgTotal = sheets.filter((s) => s.src).length;

  if (error) return <div className="p-12 text-center text-sm text-rose-600" data-testid="pack-error">{error}</div>;
  if (!data) return <div className="p-12 text-center text-sm text-zinc-500">読み込み中…</div>;

  return (
    <>
      <style>{`
        @page { size: A4; margin: 12mm; }
        @media print {
          body { background: #fff !important; }
          .no-print { display: none !important; }
          /* 改ページは「シートの前」に入れる。末尾に余分な空白ページを作らないため。 */
          .pack-sheet { break-before: page; page-break-before: always; box-shadow: none !important;
                        margin: 0 !important; padding: 0 !important; max-width: none !important; }
          .pack-sheet:first-of-type { break-before: auto; page-break-before: auto; }
          .pack-sheet, .pack-sheet * { break-after: auto; page-break-after: auto; }
          [data-testid="chat-fab"], [data-sonner-toaster], .fixed { display: none !important; }
        }
        @media screen { body { background: #f4f4f5; } }
      `}</style>

      <div className="no-print sticky top-0 z-10 bg-white border-b border-zinc-200 px-4 py-2 flex items-center justify-between shadow-sm" data-testid="pack-toolbar">
        <div className="text-sm text-zinc-600">
          {data.month_label} 請求書パック — <span className="font-semibold text-zinc-800">{data.count}件 / {yen(data.total_amount)}</span>
          {imgTotal > 0 && <span className="ml-2 text-xs text-zinc-400">請求書 {loadedImgs}/{imgTotal} 読込済み</span>}
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => window.print()}
            className="px-3 py-1.5 rounded bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-40"
            disabled={imgTotal > 0 && loadedImgs < imgTotal}
            data-testid="pack-print-btn">
            印刷 / PDF保存
          </button>
          <button type="button" onClick={() => window.close()}
            className="px-3 py-1.5 rounded border border-zinc-200 text-sm text-zinc-600 hover:bg-zinc-50"
            data-testid="pack-close-btn">
            閉じる
          </button>
        </div>
      </div>

      {/* ── 表紙 / 索引 ── */}
      <div className="pack-sheet mx-auto my-4 bg-white p-8 shadow-sm max-w-[210mm] text-[11px] text-zinc-800" data-testid="pack-cover">
        <div className="flex items-end justify-between border-b-2 border-zinc-800 pb-2">
          <div>
            <h1 className="text-lg font-bold tracking-tight">請求書パック（TECHS外 直接購入）</h1>
            <p className="text-xs text-zinc-500 mt-0.5">請求書日付で集計 — 部品受注入力</p>
          </div>
          <p className="text-base font-bold tabular-nums" data-testid="pack-month">{data.month_label}</p>
        </div>

        <div className="grid grid-cols-3 gap-3 my-4">
          <div className="rounded border border-zinc-200 p-2">
            <p className="text-[10px] text-zinc-500">件数</p>
            <p className="text-base font-bold tabular-nums" data-testid="pack-count">{data.count}件</p>
            <p className="text-[9px] text-zinc-500">
              請求書未添付 <span className={data.no_file_count ? 'font-bold text-rose-600' : ''}
                data-testid="pack-nofile-count">{data.no_file_count}件</span>
            </p>
          </div>
          <div className="rounded border border-zinc-200 p-2">
            <p className="text-[10px] text-zinc-500">支払合計（円）</p>
            <p className="text-base font-bold tabular-nums" data-testid="pack-total">{yen(data.total_amount)}</p>
            <p className="text-[9px] text-zinc-500">商品＋諸費用＋別途税</p>
          </div>
          <div className="rounded border border-zinc-200 p-2">
            <p className="text-[10px] text-zinc-500">内訳</p>
            <p className="text-[10px] tabular-nums leading-relaxed">
              商品 <span className="font-semibold">{yen(data.goods_total_amount)}</span><br />
              諸費用 <span className="font-semibold">{yen(data.extra_total_amount)}</span>
              ／税 <span className="font-semibold">{yen(data.tax_total_amount)}</span>
            </p>
          </div>
        </div>

        <table className="w-full table-fixed border-collapse">
          <colgroup>{COLS.map((w, i) => <col key={i} style={{ width: w }} />)}</colgroup>
          <thead>
            <tr className="border-b border-zinc-800 text-left text-[9px] text-zinc-500">
              <th className="py-1 pr-1 text-right">#</th>
              <th className="py-1 pr-1">請求日</th>
              <th className="py-1 pr-1">依頼No</th>
              <th className="py-1 pr-1">品名 / 調達方法・仕入先</th>
              <th className="py-1 pr-1">請求書No / 通貨</th>
              <th className="py-1 pr-1 text-right">商品</th>
              <th className="py-1 pr-1 text-right">諸費用</th>
              <th className="py-1 pr-1 text-right">税</th>
              <th className="py-1 pr-1 text-right">支払合計</th>
              <th className="py-1 text-center">請求書</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r, i) => <CoverRow key={r.request_id} r={r} i={i} />)}
            {data.rows.length === 0 && (
              <tr><td colSpan={10} className="py-6 text-center text-zinc-400" data-testid="pack-empty">この月に該当する購入はありません</td></tr>
            )}
          </tbody>
          {data.rows.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-zinc-800 font-bold">
                <td colSpan={5} className="py-1 pr-1 text-right">合計 {data.count}件</td>
                <td className="py-1 pr-1 text-right tabular-nums">{yen(data.goods_total_amount)}</td>
                <td className="py-1 pr-1 text-right tabular-nums">{yen(data.extra_total_amount)}</td>
                <td className="py-1 pr-1 text-right tabular-nums">{yen(data.tax_total_amount)}</td>
                <td className="py-1 pr-1 text-right tabular-nums" data-testid="pack-foot-total">{yen(data.total_amount)}</td>
                <td />
              </tr>
            </tfoot>
          )}
        </table>

        {/* 要修正リスト — どの月にも入らない購入。絶対に落とさない。 */}
        <div className="mt-6" data-testid="pack-missing-date-block">
          <p className="text-xs font-bold text-amber-700">
            請求書日付未設定（どの月の合計にも入りません・要修正）
            <span className="ml-1 tabular-nums" data-testid="pack-missing-count">{data.missing_date_count}件</span>
          </p>
          {data.missing_date_count === 0 ? (
            <p className="text-[10px] text-zinc-400 mt-1">なし — 入荷済みの直接購入はすべて請求書日付が入っています。</p>
          ) : (
            <table className="w-full border-collapse mt-1">
              <thead>
                <tr className="border-b border-amber-300 text-left text-[10px] text-zinc-500">
                  <th className="py-1 pr-2">依頼No</th>
                  <th className="py-1 pr-2">品名</th>
                  <th className="py-1 pr-2">調達方法</th>
                  <th className="py-1 pr-2">注文先 / 仕入先</th>
                  <th className="py-1 pr-2 text-right">金額</th>
                  <th className="py-1">請求書</th>
                </tr>
              </thead>
              <tbody>
                {data.missing_date_rows.map((r) => (
                  <tr key={r.request_id} className="border-b border-zinc-100" data-testid={`pack-missing-${r.request_no}`}>
                    <td className="py-1 pr-2 whitespace-nowrap font-medium">{r.request_no}</td>
                    <td className="py-1 pr-2">
                      {r.item_summary}
                      {r.line_count > 1 && <span className="text-zinc-500"> 他{r.line_count - 1}件</span>}
                    </td>
                    <td className="py-1 pr-2 whitespace-nowrap">{r.method_label}</td>
                    <td className="py-1 pr-2">{r.vendor || '—'}</td>
                    <td className="py-1 pr-2 text-right tabular-nums">{yen(r.amount)}</td>
                    <td className="py-1 whitespace-nowrap">
                      {r.has_invoice_file ? '添付あり' : <span className="font-bold text-rose-600">請求書未添付</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── 請求書 各1枚 ── */}
      {sheets.map((s, i) => (
        <div key={`${s.row.request_id}-${s.file.url}-${s.page || 0}`}
          className="pack-sheet mx-auto my-4 bg-white p-8 shadow-sm max-w-[210mm]"
          data-testid={`pack-invoice-sheet-${i}`}>
          <div className="flex items-end justify-between border-b border-zinc-300 pb-1 mb-3 text-[11px]">
            <p className="font-bold">
              {s.row.request_no}
              <span className="ml-2 font-normal text-zinc-600">{s.row.item_summary}</span>
            </p>
            <p className="text-zinc-500">
              {s.row.invoice_date || '—'} · {s.row.method_label} · {yen(s.row.amount)}
              {s.page ? ` · P${s.page}/${s.file.pages}` : ''}
            </p>
          </div>
          <p className="text-[10px] text-zinc-400 mb-2 break-all">{s.file.filename}</p>
          {s.src ? (
            <img src={s.src} alt={s.file.filename}
              className="mx-auto block max-w-full max-h-[245mm] w-auto h-auto object-contain"
              onLoad={() => setLoadedImgs((n) => n + 1)}
              onError={() => setLoadedImgs((n) => n + 1)}
              data-testid={`pack-invoice-img-${i}`} />
          ) : (
            <div className="rounded-lg border border-rose-200 bg-rose-50 p-4"
              data-testid={`pack-invoice-unavailable-${i}`}>
              <p className="text-xs font-semibold text-rose-700">
                このPDFをこのページに差し込めませんでした — 下のボタンから個別に印刷してください
              </p>
              <p className="mt-1 text-[11px] text-rose-600" data-testid={`pack-invoice-reason-${i}`}>
                原因: {s.file.error || '不明（サーバーのログをご確認ください）'}
              </p>
              <span className="no-print mt-2 flex flex-wrap items-center gap-2">
                <a href={`${BASE}${s.file.url}`} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-md bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-700"
                  data-testid={`pack-invoice-open-${i}`}>
                  請求書PDFを開く（別タブで印刷）
                </a>
                <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-rose-300 bg-white px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-100">
                  <input type="file" accept="application/pdf,image/*" className="hidden"
                    onChange={(e) => replaceInvoice(s.row.request_id, e.target.files?.[0])}
                    data-testid={`pack-invoice-replace-${i}`} />
                  {replacing === s.row.request_id ? '差し替え中…' : '請求書PDFを差し替えて読み込む'}
                </label>
              </span>
              <p className="no-print mt-1 text-[10px] text-rose-500">
                差し替えると現在の保存先（設定 → ストレージ）に保存され、このページで印刷できるようになります
              </p>
            </div>
          )}
        </div>
      ))}
    </>
  );
}
